import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from 'hooks/useAuth';
import { streamChatWithAI } from 'services/api';
import toast from 'react-hot-toast';

const QUICK_PROMPTS = [
  'What to do in a fire?',
  'I am having a medical emergency',
  'Flood near me — what now?',
  'How do I find the nearest safe zone?',
];

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 px-4 py-3 chat-ai w-16">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="w-1.5 h-1.5 bg-electric rounded-full animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  );
}

export default function AIChatAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '👋 I\'m AegisNet AI — your emergency response assistant. How can I help you stay safe right now?',
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const { user } = useAuth();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const sendMessage = async (text) => {
    const content = text || input.trim();
    if (!content || loading) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content }]);
    setLoading(true);

    let aiResponse = '';
    setMessages((prev) => [...prev, { role: 'assistant', content: '', streaming: true }]);

    try {
      await streamChatWithAI(
        content,
        user?.uid || 'anonymous',
        { recentAlerts: [] },
        (token) => {
          aiResponse += token;
          setMessages((prev) => {
            const updated = [...prev];
            updated[updated.length - 1] = { role: 'assistant', content: aiResponse, streaming: true };
            return updated;
          });
        }
      );
      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: aiResponse, streaming: false };
        return updated;
      });
    } catch {
      // Fallback response if backend unavailable
      const fallback = getFallbackResponse(content);
      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: fallback, streaming: false };
        return updated;
      });
    } finally {
      setLoading(false);
    }
  };

  const getFallbackResponse = (query) => {
    const q = query.toLowerCase();
    if (q.includes('fire')) return '🔥 **Fire Emergency Protocol:**\n1. Alert everyone — shout "FIRE!"\n2. Activate nearest fire alarm\n3. Call 101 immediately\n4. Do NOT use elevators\n5. Crawl low under smoke\n6. Meet at assembly point\n\n📍 Nearest resource: Bhubaneswar Fire Station — 0.8km, ETA 4 min';
    if (q.includes('medical') || q.includes('heart') || q.includes('breathing')) return '🚑 **Medical Emergency Protocol:**\n1. Call 108 (Emergency Ambulance)\n2. Keep person calm and still\n3. Do NOT give food/water\n4. If unconscious: check breathing\n5. Start CPR if no pulse\n\n📍 Nearest: AIIMS Bhubaneswar — 2.1km, ETA 6 min';
    if (q.includes('flood')) return '🌊 **Flood Emergency Protocol:**\n1. Move to higher ground immediately\n2. Do NOT walk in moving water\n3. Call 0674-2534177 (Flood Control)\n4. Disconnect electrical appliances\n5. Follow evacuation route\n\n📍 Nearest Safe Zone: KIIT Ground — 1.2km (2000 capacity)';
    return 'I\'m here to help with emergency guidance. Please describe your situation in detail and I\'ll provide step-by-step instructions. For immediate danger, call 112 (Emergency) or 108 (Ambulance).';
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating button */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-electric shadow-glow-blue flex items-center justify-center text-2xl"
        aria-label="Open AI assistant"
        title="AI Emergency Assistant"
      >
        🤖
      </motion.button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            className="fixed bottom-24 right-6 z-40 w-80 md:w-96 h-[520px] glass-card flex flex-col overflow-hidden"
            role="dialog"
            aria-label="AI Emergency Assistant"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-navy-border bg-navy-card">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-electric flex items-center justify-center text-sm">🤖</div>
                <div>
                  <div className="font-display font-bold text-sm text-electric">AegisNet AI</div>
                  <div className="text-xs text-emerald flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald animate-pulse" />
                    Online · Emergency Mode
                  </div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-gray-400 hover:text-white text-lg"
                aria-label="Close chat"
              >
                ✕
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      msg.role === 'user' ? 'chat-user text-white' : 'chat-ai text-gray-200'
                    }`}
                  >
                    {msg.content}
                    {msg.streaming && (
                      <span className="inline-block w-1 h-4 bg-electric ml-0.5 animate-pulse" />
                    )}
                  </div>
                </motion.div>
              ))}
              {loading && messages[messages.length - 1]?.role !== 'assistant' && (
                <div className="flex justify-start">
                  <TypingIndicator />
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Quick prompts */}
            <div className="px-4 py-2 border-t border-navy-border flex gap-2 overflow-x-auto">
              {QUICK_PROMPTS.slice(0, 2).map((p) => (
                <button
                  key={p}
                  onClick={() => sendMessage(p)}
                  className="flex-shrink-0 text-xs px-3 py-1.5 rounded-full border border-electric/30 text-electric hover:bg-electric/10 transition-colors"
                >
                  {p.length > 20 ? p.slice(0, 20) + '…' : p}
                </button>
              ))}
            </div>

            {/* Input */}
            <div className="px-4 pb-4 pt-2 flex gap-2">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Describe your emergency..."
                className="flex-1 bg-navy border border-navy-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-electric"
                aria-label="Message input"
                disabled={loading}
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                className="px-3 py-2 bg-electric rounded-lg text-navy font-bold text-sm disabled:opacity-50 hover:brightness-110 transition-all"
                aria-label="Send message"
              >
                ↑
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
